/**
 * The keyboard model (§15 G7): `?` opens this cheatsheet anywhere, `/` jumps to the page's search, and each
 * working surface lists its own keys. The same list is what the surfaces implement — nothing here is aspirational.
 */
import { useEffect, useState } from 'react';
import { Keyboard } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';

const GROUPS: Array<{ title: string; keys: Array<[string, string]> }> = [
  { title: 'Everywhere', keys: [['Ctrl/⌘ + K', 'Go to any page, event, property or setting (outside the floor-plan studio, where K is the ruler)'], ['?', 'Show these shortcuts'], ['/', 'Jump to the search box on this page'], ['Esc', 'Close a dialog, panel or menu'], ['Tab', 'Move between controls; a “Skip to content” link comes first']] },
  { title: 'Room assignments', keys: [['← → ↑ ↓', 'Move between rooms on the plan'], ['Home / End', 'First / last room'], ['Enter or Space', 'Open a room, or place the chosen guests in it'], ['Esc', 'Cancel placing'], ['Ctrl/⌘ + Z', 'Undo the last move'], ['Ctrl/⌘ + Shift + Z or Ctrl/⌘ + Y', 'Redo']] },
  { title: 'Floor-plan studio', keys: [['Ctrl/⌘ + S', 'Save'], ['Ctrl/⌘ + Z', 'Undo'], ['Ctrl/⌘ + Shift + Z or Ctrl/⌘ + Y', 'Redo'], ['Ctrl/⌘ + D', 'Duplicate the selection'], ['Ctrl/⌘ + A', 'Select every room'], ['Alt + arrow', 'Add a room next to the selected one'], ['Ctrl/⌘ + C, then Ctrl/⌘ + V', 'Copy, then paste (onto any floor)'], ['Shift + arrow', 'Nudge the selection one grid step'], ['Shift + Alt + arrow', 'Nudge it one pixel'], ['Shift + Ctrl/⌘ + arrow', 'Move it one room over'], ['Shift + click', 'Add a room to the selection'], ['Delete', 'Delete the selection'], ['Esc', 'Back to the select tool, then clear the selection']] },
  { title: 'Check-in desk', keys: [['/', 'Search guests']] },
];

const typing = (t: EventTarget | null) => Boolean((t as HTMLElement | null)?.closest?.('input, textarea, select, [contenteditable="true"]'));

export function ShortcutsButton() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || typing(e.target)) return;
      if (e.key === '?') { e.preventDefault(); setOpen(true); }
      else if (e.key === '/') {
        const search = document.querySelector<HTMLInputElement>('main input[type="search"]');
        if (search) { e.preventDefault(); search.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <>
      <button className="hidden size-9 items-center justify-center rounded-control text-ink-2 hover:bg-surface-2 hover:text-ink md:inline-flex" aria-label="Keyboard shortcuts" title="Keyboard shortcuts (?)" onClick={() => setOpen(true)} data-testid="shortcuts-button">
        <Keyboard className="size-4" aria-hidden />
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Keyboard shortcuts" size="lg">
        <div className="grid gap-6 sm:grid-cols-2" data-testid="shortcuts">
          {GROUPS.map((g) => (
            <section key={g.title}>
              <h3 className="mb-2 text-[13px] font-semibold text-ink-2">{g.title}</h3>
              <dl className="flex flex-col gap-1.5 text-sm">
                {g.keys.map(([k, d]) => (
                  <div key={k + d} className="flex items-baseline justify-between gap-3">
                    <dt><kbd className="rounded-[4px] bg-surface-2 px-1.5 py-0.5 font-mono text-[12px] text-ink ring-1 ring-line">{k}</kbd></dt>
                    <dd className="text-right text-ink-2">{d}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </Dialog>
    </>
  );
}
